import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../services/HttpManager";
import { ProspectFilters, PromoBulkPerson, WaChannel, WaTemplate } from "../../../services/v2/prospects.service";
import { errorTexto } from "./salesHelpers";

/** Plantillas de promo primero: en español, con "promo" en el nombre, de marketing. */
const ordenPromo = (ts: WaTemplate[]) => [...ts].sort((a, b) => {
	const pa = (/^es/i.test(a.language) ? 4 : 0) + (/promo/i.test(a.name) ? 2 : 0) + (a.category === "MARKETING" ? 1 : 0);
	const pb = (/^es/i.test(b.language) ? 4 : 0) + (/promo/i.test(b.name) ? 2 : 0) + (b.category === "MARKETING" ? 1 : 0);
	return pb - pa || a.name.localeCompare(b.name);
});

export const rellenar = (body: string, params: string[]) => body.replace(/\{\{(\d+)\}\}/g, (m, i) => params[Number(i) - 1] ?? m);

/**
 * Elegir número y plantilla aprobada de Meta. Las variables se llenan en este orden (acordado para las de promo):
 * {{1}} saludo y nombre · {{2}} curso · {{3}} a pagar con la promo · {{4}} sin la promo · {{5}} hasta cuándo.
 */
export const PlantillaPicker: React.FC<{
	campusId: number; channels: WaChannel[]; channelId: number | null; onChannel: (id: number) => void;
	template: WaTemplate | null; onTemplate: (t: WaTemplate | null) => void;
}> = ({ campusId, channels, channelId, onChannel, template, onTemplate }) => {
	const http = HttpManager.getInstance();
	const [ts, setTs] = useState<WaTemplate[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		if (!channelId) return;
		setTs(null); setError(null); onTemplate(null);
		http.prospectsService.waTemplates(campusId, channelId).then((r) => {
			const o = ordenPromo(r.data.templates || []);
			setTs(o);
			if (o.length) onTemplate(o[0]);
		}).catch((e) => setError(errorTexto(e)));
	}, [campusId, channelId]);
	return (
		<div className="pb-picker">
			<label>Número
				<select value={channelId || ""} onChange={(e) => onChannel(Number(e.target.value))}>
					{channels.map((c) => <option key={c.id} value={c.id}>{(c.label || c.name || "") + " · " + c.display_phone}</option>)}
				</select>
			</label>
			<label>Plantilla
				<select value={template ? template.name + "|" + template.language : ""} disabled={!ts}
					onChange={(e) => onTemplate((ts || []).find((t) => t.name + "|" + t.language === e.target.value) || null)}>
					{!ts ? <option>Cargando…</option> : null}
					{(ts || []).map((t) => <option key={t.name + t.language} value={t.name + "|" + t.language}>{t.name} · {t.language}{t.category ? " · " + t.category.toLowerCase() : ""}</option>)}
				</select>
			</label>
			{ts && !ts.length ? <div className="aviso">Este número no tiene plantillas aprobadas.</div> : null}
			{error ? <div className="aviso error">{error}</div> : null}
		</div>
	);
};

interface Props { campusId: number; filtros: ProspectFilters; onClose: () => void; }

/**
 * "Enviar promo a los que aplican" (lienzo Cartera). Paso 1: quién califica, con sus datos, sin mandar nada.
 * Paso 2: la supervisora elige la plantilla, ve el mensaje ya lleno y confirma. Se manda por tandas de 25.
 */
const PromoBloqueModal: React.FC<Props> = ({ campusId, filtros, onClose }) => {
	const http = HttpManager.getInstance();
	const [people, setPeople] = useState<PromoBulkPerson[] | null>(null);
	const [quitados, setQuitados] = useState<number[]>([]);
	const [channels, setChannels] = useState<WaChannel[]>([]);
	const [channelId, setChannelId] = useState<number | null>(null);
	const [template, setTemplate] = useState<WaTemplate | null>(null);
	const [confirmar, setConfirmar] = useState<boolean>(false);
	const [enviando, setEnviando] = useState<boolean>(false);
	const [hechos, setHechos] = useState<{ id: number; ok: boolean; error?: string }[]>([]);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		http.prospectsService.promoBulkPreview(campusId, filtros).then((r) => setPeople(r.data.people)).catch((e) => setError(errorTexto(e)));
		http.prospectsService.waChannels(campusId).then((r) => {
			const activos = (r.data.channels || []).filter((c: any) => c.bool_enabled);
			const delPlantel = activos.filter((c: any) => c.campus_id === campusId);
			const cs = delPlantel.length ? delPlantel : activos;
			setChannels(cs);
			const propio = cs.find((c: any) => c.campus_id === campusId && c.dept === "ventas") || cs.find((c: any) => c.campus_id === campusId) || cs[0];
			if (propio) setChannelId(propio.id);
		}).catch((e) => setError(errorTexto(e)));
	}, [campusId]);

	const lista = (people || []).filter((p) => !quitados.includes(p.id));
	const ejemplo = lista[0];

	const enviar = async () => {
		if (!template || !lista.length) return;
		setEnviando(true); setError(null);
		const out: typeof hechos = [];
		try {
			for (let i = 0; i < lista.length; i += 25) {
				const tanda = lista.slice(i, i + 25).map((p) => ({ id: p.id, params: p.params }));
				const r = await http.prospectsService.promoBulkSend(campusId, tanda, template.name, template.language, channelId);
				out.push(...r.data.results);
				setHechos([...out]);
			}
		} catch (e) { setError(errorTexto(e)); }
		finally { setEnviando(false); setConfirmar(false); }
	};

	const ok = hechos.filter((h) => h.ok).length;
	const fallas = hechos.filter((h) => !h.ok);

	return (
		<Modal show onHide={enviando ? undefined : onClose} size="xl" className="ventas-nuevo-modal">
			<Modal.Header closeButton={!enviando}>
				<Modal.Title style={{ fontFamily: "Bricolage, sans-serif", fontWeight: 800 }}>Enviar promo a los que aplican</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{error ? <div className="aviso error">{error}</div> : null}
				{!people ? <div className="ventas-vacio">Buscando a quién le aplica…</div> : !people.length ? (
					<div className="aviso">Nadie de esta cartera tiene hoy una promo que le ahorre algo.</div>
				) : (
					<div className="pm-cuerpo">
						<div className="pm-izq">
							<div className="apagado">{lista.length} de {people.length} matriculadas con WhatsApp y una promo que les ahorra (hasta 200 por tanda). Quita a quien no quieras.</div>
							<div className="pb-lista">
								{people.map((p) => {
									const h = hechos.find((x) => x.id === p.id);
									return (
										<label key={p.id} className={"pb-persona" + (quitados.includes(p.id) ? " fuera" : "")}>
											<input type="checkbox" checked={!quitados.includes(p.id)} disabled={enviando || !!hechos.length}
												onChange={(e) => setQuitados(e.target.checked ? quitados.filter((x) => x !== p.id) : [...quitados, p.id])} />
											<span className="pb-n">{p.name}</span>
											<span className="apagado">{p.promo} · {p.params[2]}</span>
											{h ? <span className={h.ok ? "verde" : "oro"}>{h.ok ? "enviado" : h.error}</span> : null}
										</label>
									);
								})}
							</div>
						</div>
						<div className="pm-der">
							<PlantillaPicker campusId={campusId} channels={channels} channelId={channelId} onChannel={setChannelId} template={template} onTemplate={setTemplate} />
							{template && ejemplo ? (
								<>
									<h4>Así le llega a {ejemplo.name.split(" ")[0]}</h4>
									<div className="pb-vista">{rellenar(template.body, ejemplo.params)}</div>
									{template.params > 5 ? <div className="aviso">Esta plantilla pide {template.params} datos; aquí se llenan 5. Elige una de promo.</div> : null}
								</>
							) : null}
							{hechos.length ? <div className="aviso">{ok} enviados{fallas.length ? ` · ${fallas.length} no se pudieron (ver la lista)` : ""}. Cada uno quedó en su bitácora.</div> : null}
						</div>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				{confirmar ? (
					<>
						<span className="oro" style={{ marginRight: "auto" }}>Se mandará la plantilla «{template?.name}» a {lista.length} personas por WhatsApp. No se puede deshacer.</span>
						<button className="btn-wa" disabled={enviando} onClick={() => setConfirmar(false)}>No</button>
						<button className="btn-wa pri" disabled={enviando} onClick={enviar}>{enviando ? `Enviando… ${hechos.length}/${lista.length}` : "Sí, mandar"}</button>
					</>
				) : (
					<>
						<button className="btn-wa" onClick={onClose}>{hechos.length ? "Cerrar" : "Cancelar"}</button>
						{!hechos.length ? <button className="btn-wa pri" disabled={!template || !lista.length || template.params > 5} onClick={() => setConfirmar(true)}>Mandar a {lista.length}</button> : null}
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default PromoBloqueModal;
